import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Button, IconButton, FormControlLabel, Switch, Chip, FormControl, Tooltip } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import DocumentTypesDrawer from './DocumentTypesDrawer';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateSectionTitleSx,
  harmonatePlainIconButtonSx,
  harmonateSwitchSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
} from '../../../theme/harmonateStyles';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const Tenant = () => {
  const { canAdd, canEdit } = useFunctionPermissions(FC.TENANT);
  const [tenants, setTenants] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedRows, setEditedRows] = useState({});
  const [originalRows, setOriginalRows] = useState({});
  const [form, setForm] = useState({
    name: '',
    clientId: '',
    s3KmsKeyId: '',
    maxClassificationPages: '',
    address: '',
    phone: '',
    database: '',
    status: 'ACTIVE',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    integrationId: '',
    case_sensitive: false,
    mfa_enabled: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [formError, setFormError] = useState(null);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) throw new Error(`Failed to fetch tenants: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      const tenantsWithIds = Array.isArray(data) ? data : [];
      setTenants(tenantsWithIds);
      
      // Initialize original rows for editing
      const originalData = {};
      tenantsWithIds.forEach(tenant => {
        originalData[tenant.id] = { ...tenant };
      });
      console.log('Setting originalRows:', originalData);
      setOriginalRows(originalData);
    } catch (err) {
      setError(err.message);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) throw new Error(`Failed to fetch clients: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching clients:', err.message);
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  };

  const handleOpenDialog = () => {
    if (!canAdd) return;
    setOpenDialog(true);
    // Fetch clients when dialog opens
    if (clients.length === 0) {
      fetchClients();
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
    setForm({ 
      name: '', 
      clientId: '', 
      s3KmsKeyId: '', 
      maxClassificationPages: '', 
      address: '', 
      phone: '', 
      database: '', 
      status: 'ACTIVE',
      adminEmail: '',
      adminFirstName: '',
      adminLastName: '',
      integrationId: '',
      case_sensitive: false,
      mfa_enabled: false,
    });
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear form error when user starts typing, especially for email field
    if (e.target.name === 'adminEmail' && formError) {
      setFormError(null);
    }
  };

  const handleSwitchChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canAdd) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenants`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name,
          clientId: form.clientId,
          s3KmsKeyId: form.s3KmsKeyId,
          maxClassificationPages: form.maxClassificationPages ? Number(form.maxClassificationPages) : undefined,
          address: form.address,
          phone: form.phone,
          database: form.database,
          integrationId: form.integrationId || '',
          status: form.status,
          adminEmail: form.adminEmail,
          adminUsername: form.adminEmail, // Use email as username
          adminFirstName: form.adminFirstName,
          adminLastName: form.adminLastName,
          caseSensitive: form.case_sensitive,
          mfaEnabled: form.mfa_enabled,
        }),
      });
      
      if (!resp.ok) {
        const errorData = await resp.json();
        const errorMessage = errorData.message || 'Failed to create tenant';
        
        // Handle specific duplicate email error
        if (errorMessage.includes('already exists') && errorMessage.includes('email')) {
          setFormError('This email address is already registered. Please use a different email address.');
        } else {
          setFormError(errorMessage);
        }
        return;
      }
      
      handleCloseDialog();
      fetchTenants();
    } catch (err) {
      setFormError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = (params) => {
    // Only open drawer if not in edit mode
    if (!isEditMode) {
      setSelectedTenantId(params.row.id);
      setDrawerOpen(true);
    }
  };

  const handleEditModeToggle = () => {
    if (isEditMode) {
      // Exit edit mode - discard changes
      setEditedRows({});
      setIsEditMode(false);
    } else {
      if (!canEdit) return;
      setIsEditMode(true);
    }
  };

  const handleProcessRowUpdate = async (newRow, oldRow) => {
    if (!canEdit) return oldRow;
    console.log('Row update triggered:', { newRow, oldRow });
    console.log('All originalRows:', originalRows);
    console.log('Looking for tenant with ID:', newRow.id);
    
    // Find which field was changed
    const changedField = Object.keys(newRow).find(key => newRow[key] !== oldRow[key]);
    if (!changedField) return newRow;

    console.log('Field changed:', changedField, 'from', oldRow[changedField], 'to', newRow[changedField]);

    // Auto-save the cell change
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Get the complete original tenant data from the API response
      let originalTenant = originalRows[newRow.id];
      console.log('Original tenant found:', originalTenant);
      
      if (!originalTenant) {
        console.error('Original tenant data not found for ID:', newRow.id);
        console.error('Available originalRows keys:', Object.keys(originalRows));
        
        // Fallback: try to find the tenant in the current tenants array
        const fallbackTenant = tenants.find(t => t.id === newRow.id);
        if (fallbackTenant) {
          console.log('Using fallback tenant data:', fallbackTenant);
          originalTenant = fallbackTenant;
        } else {
          throw new Error('Original tenant data not found');
        }
      }

      // Prepare the complete data for the API call by merging original data with changes
      // Convert snake_case fields from GET API to camelCase for PUT API
      const completeApiData = {
        name: newRow.name,
        clientId: originalTenant.client_id,
        s3KmsKeyId: newRow.s3KmsKeyId || originalTenant.s3_kms_key_id || originalTenant.s3KmsKeyId,
        maxClassificationPages: newRow.maxClassificationPages || originalTenant.max_classification_pages || originalTenant.maxClassificationPages,
        integrationId: newRow.integrationId || newRow.integration_id || originalTenant.integration_id || '',
        applicationId: originalTenant.application_id || originalTenant.applicationId,
        address: newRow.address,
        database: newRow.database,
        phone: newRow.phone,
        status: newRow.status,
        caseSensitive: newRow.case_sensitive !== undefined ? newRow.case_sensitive : (originalTenant.case_sensitive || false),
        mfaEnabled: newRow.mfa_enabled !== undefined ? newRow.mfa_enabled : (originalTenant.mfa_enabled || false)
      };

      console.log('Original tenant client_id:', originalTenant.client_id);
      console.log('Sending PUT request to:', `${process.env.REACT_APP_BACKEND_URL}conductor/tenants/${newRow.id}`);
      console.log('Complete API Data:', completeApiData);

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenants/${newRow.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completeApiData),
      });

      console.log('API Response status:', resp.status);

      if (!resp.ok) {
        const errorData = await resp.text();
        console.error('API Error:', errorData);
        throw new Error(`Failed to update tenant: ${resp.status} ${resp.statusText}`);
      }

      console.log('Successfully updated tenant');
      
      // Update the original rows with the new data
      setOriginalRows(prev => ({
        ...prev,
        [newRow.id]: {
          ...originalTenant,
          ...newRow
        }
      }));

      // Refresh the tenants list to show updated data
      fetchTenants();
      
      return newRow;
    } catch (err) {
      console.error('Error updating tenant:', err);
      // Return the old row to revert the change
      return oldRow;
    }
  };

  const handleProcessRowUpdateError = (error) => {
    console.error('Row update error:', error);
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>Loading tenants...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load tenants
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchTenants}
          sx={harmonateProceedButtonSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const columns = [
    { 
      field: 'name', 
      headerName: 'Company Name', 
      flex: 1,
      editable: isEditMode,
    },
    { 
      field: 'tenantBucket', 
      headerName: 'Tenant Bucket', 
      flex: 1,
      editable: false,
    },
    { 
      field: 'address', 
      headerName: 'Address', 
      flex: 1,
      editable: isEditMode,
    },
    { 
      field: 'phone', 
      headerName: 'Phone', 
      flex: 1,
      editable: isEditMode,
    },
    { 
      field: 'database', 
      headerName: 'Database', 
      flex: 1,
      editable: isEditMode,
    },
    { 
      field: 'integration_id', 
      headerName: 'Integration ID', 
      flex: 1,
      editable: isEditMode,
    },
    { 
      field: 's3KmsKeyId', 
      headerName: 'KMS Key', 
      flex: 1,
      editable: isEditMode,
    },
    { 
      field: 'maxClassificationPages', 
      headerName: 'Max Classification Pages', 
      flex: 1,
      editable: isEditMode,
      type: 'number',
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 1,
      editable: isEditMode,
      type: 'singleSelect',
      valueOptions: ['ACTIVE', 'INACTIVE'],
    },
    {
      field: 'case_sensitive',
      headerName: 'Case Sensitive',
      width: 150,
      editable: isEditMode,
      type: 'boolean',
      renderCell: (params) => {
        const value = params.value;
        const isCaseSensitive = value === true || value === 'true' || value === 1;
        
        return (
          <Chip
            label={isCaseSensitive ? 'Yes' : 'No'}
            size="small"
            sx={{
              bgcolor: isCaseSensitive ? ht.secondaryTab : ht.listBg,
              color: isCaseSensitive ? ht.text : ht.text,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        );
      },
      renderEditCell: (params) => {
        const { id, value, field } = params;
        const isChecked = value === true || value === 'true' || value === 1;
        
        return (
          <FormControlLabel
            control={
              <Switch
                checked={isChecked}
                onChange={(event) => {
                  params.api.setEditCellValue({ id, field, value: event.target.checked });
                }}
                size="small"
                sx={harmonateSwitchSx}
              />
            }
            label=""
            sx={{ margin: 0 }}
          />
        );
      }
    },
    {
      field: 'mfa_enabled',
      headerName: 'MFA',
      width: 120,
      editable: isEditMode,
      type: 'boolean',
      renderCell: (params) => {
        const value = params.value;
        const isMfaEnabled = value === true || value === 'true' || value === 1;
        
        return (
          <Chip
            label={isMfaEnabled ? 'Enabled' : 'Disabled'}
            size="small"
            sx={{
              bgcolor: isMfaEnabled ? ht.secondaryTab : ht.listBg,
              color: ht.text,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        );
      },
      renderEditCell: (params) => {
        const { id, value, field } = params;
        const isChecked = value === true || value === 'true' || value === 1;
        
        return (
          <FormControlLabel
            control={
              <Switch
                checked={isChecked}
                onChange={(event) => {
                  params.api.setEditCellValue({ id, field, value: event.target.checked });
                }}
                size="small"
                sx={harmonateSwitchSx}
              />
            }
            label=""
            sx={{ margin: 0 }}
          />
        );
      }
    },
  ];

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Tenants
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={canEdit ? (isEditMode ? 'Exit Edit Mode' : 'Enter Edit Mode') : 'No edit permission'}>
              <span>
                <IconButton
                  onClick={handleEditModeToggle}
                  disabled={!canEdit}
                  sx={{
                    ...harmonatePlainIconButtonSx,
                    color: isEditMode ? ht.secondaryTab : ht.white,
                  }}
                >
                  <EditOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add New Tenant' : 'No add permission'}>
              <span>
                <IconButton
                  onClick={handleOpenDialog}
                  disabled={!canAdd}
                  sx={harmonatePlainIconButtonSx}
                >
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
      </Box>
      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, mb: 3, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <DataGridPro
          rows={tenants.map((t, idx) => ({
            id: t.id || idx + 1,
            name: t.name || '',
            tenantBucket: t.id ? `t-${t.id}` : '',
            integration_id: t.integration_id || '',
            address: t.address || '',
            phone: t.phone || '',
            database: t.database || '',
            s3KmsKeyId: t.s3_kms_key_id || t.s3KmsKeyId || '',
            maxClassificationPages: t.max_classification_pages || t.maxClassificationPages || '',
            status: t.status || '',
            case_sensitive: t.case_sensitive !== undefined ? t.case_sensitive : false,
            mfa_enabled: t.mfa_enabled !== undefined ? t.mfa_enabled : false,
          }))}
          columns={columns}
          rowHeight={40}
          autoHeight={false}
          columnResizeMode="onEnd"
          resizable
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': {
              bgcolor: isEditMode ? 'transparent' : ht.listBg,
              cursor: isEditMode ? 'default' : 'pointer',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-cell--editable': {
              cursor: 'text',
              '&:hover': {
                backgroundColor: 'rgba(46, 166, 160, 0.08)',
              },
            },
          }}
          disableSelectionOnClick={true}
          getRowId={(row) => row.id}
          onRowClick={handleRowClick}
          processRowUpdate={handleProcessRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          editMode="cell"
          onCellClick={(params, event) => {
            // In edit mode, allow cell editing on single click
            if (isEditMode && params.field && columns.find(col => col.field === params.field)?.editable) {
              event.stopPropagation();
            }
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No tenants available
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchTenants}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            )
          }}
        />
      </Paper>
      <DocumentTypesDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} tenantId={selectedTenantId} />
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add Tenant</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            {/* Tenant Information Section */}
            <Typography variant="h6" sx={{ ...harmonateSectionTitleSx, mt: 0, mb: 1, fontSize: '1.1rem' }}>
              Tenant Information
            </Typography>
            
            {/* Row 1: Company Name + Client */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="Company Name" 
                name="name" 
                value={form.name} 
                onChange={handleFormChange} 
                required 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
              <TextField 
                label="Client" 
                name="clientId" 
                value={form.clientId} 
                onChange={handleFormChange} 
                required 
                select 
                fullWidth
                size="small"
                disabled={clientsLoading}
                helperText={clientsLoading ? "Loading clients..." : ""}
                InputLabelProps={harmonateInputLabelProps}
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                sx={harmonateTextFieldSx}
              >
                {clientsLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Loading...
                  </MenuItem>
                ) : (
                  clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Box>
            
            {/* Row 2: S3 KMS Key + Max Classification Pages */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="S3 KMS Key ID" 
                name="s3KmsKeyId" 
                value={form.s3KmsKeyId} 
                onChange={handleFormChange} 
                required 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
              <TextField 
                label="Max Classification Pages" 
                name="maxClassificationPages" 
                value={form.maxClassificationPages} 
                onChange={handleFormChange} 
                type="number" 
                required 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
            </Box>
            
            {/* Row 3: Address + Phone */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="Address" 
                name="address" 
                value={form.address} 
                onChange={handleFormChange} 
                required 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
              <TextField 
                label="Phone" 
                name="phone" 
                value={form.phone} 
                onChange={handleFormChange} 
                required 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
            </Box>
            
            {/* Row 4: Database + Status */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="Database" 
                name="database" 
                value={form.database} 
                onChange={handleFormChange} 
                required 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
              <TextField 
                label="Status" 
                name="status" 
                value={form.status} 
                onChange={handleFormChange} 
                required 
                select 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                sx={harmonateTextFieldSx}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </TextField>
            </Box>

            {/* Row 5: Integration ID */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="Integration ID" 
                name="integrationId" 
                value={form.integrationId || ''}
                onChange={handleFormChange} 
                fullWidth
                required 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
                placeholder="Enter integration ID"
              />
            </Box>

            {/* Row 6: Case Sensitive + MFA */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.case_sensitive}
                    onChange={handleSwitchChange}
                    name="case_sensitive"
                    sx={harmonateSwitchSx}
                  />
                }
                label="Case Sensitive"
                sx={{ 
                  '& .MuiFormControlLabel-label': { 
                    fontSize: '0.875rem' 
                  } 
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.mfa_enabled}
                    onChange={handleSwitchChange}
                    name="mfa_enabled"
                    sx={harmonateSwitchSx}
                  />
                }
                label="Enable MFA for tenant"
                sx={{ 
                  '& .MuiFormControlLabel-label': { 
                    fontSize: '0.875rem' 
                  } 
                }}
              />
            </Box>

            {/* Admin User Information Section */}
            <Typography variant="h6" sx={{ ...harmonateSectionTitleSx, mt: 1.5, mb: 1, fontSize: '1.1rem' }}>
              Admin User Information
            </Typography>
            
            {/* Row 5: Admin Email */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="Admin Email" 
                name="adminEmail" 
                type="email"
                value={form.adminEmail} 
                onChange={handleFormChange} 
                required 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
            </Box>
            
            {/* Row 6: First Name + Last Name */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField 
                label="Admin First Name" 
                name="adminFirstName" 
                value={form.adminFirstName} 
                onChange={handleFormChange} 
                required 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
              <TextField 
                label="Admin Last Name" 
                name="adminLastName" 
                value={form.adminLastName} 
                onChange={handleFormChange} 
                required 
                fullWidth 
                size="small"
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />
            </Box>
            
            {/* Error Message Display */}
            {formError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {formError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting || clientsLoading} sx={harmonateProceedButtonSx}>
              {submitting ? 'Creating Tenant...' : 'Create Tenant'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Tenant;